import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Billing.css';

const API = (path) =>
    process.env.NODE_ENV === 'development'
        ? `http://localhost:5001/api${path}`
        : `/api${path}`;

const Billing = ({ onBack, isSuperUser, currentUser, companySlug }) => {
    const [billings, setBillings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const ITEMS_PER_PAGE = 10;

    // Filter states
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterTracking, setFilterTracking] = useState('');
    const [filterRecipient, setFilterRecipient] = useState('');
    const [filterUser, setFilterUser] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const filterTimeoutRef = useRef(null);

    useEffect(() => {
        fetchBillings(1, true);
    }, [companySlug]);

    const fetchBillings = async (pageNum = 1, reset = false, activeFilters = null) => {
        try {
            if (reset) {
                setLoading(true);
                setBillings([]);
            } else {
                setLoadingMore(true);
            }

            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const apiPath = companySlug ? `/companies/${companySlug}/billings` : '/billing/list';

            // Build filters
            const currentFilters = activeFilters || {
                dateFrom: filterDateFrom,
                dateTo: filterDateTo,
                tracking: filterTracking,
                recipient: filterRecipient,
                createdBy: filterUser,
                status: filterStatus
            };

            const params = {
                page: pageNum,
                limit: ITEMS_PER_PAGE,
                ...currentFilters
            };

            const res = await axios.get(API(apiPath), { headers, params });

            if (res.data.success) {
                const newBillings = res.data.billings || [];
                const total = res.data.totalCount || 0;

                let updatedBillings;
                if (reset) {
                    updatedBillings = newBillings;
                    setBillings(updatedBillings);
                } else {
                    updatedBillings = [...billings, ...newBillings];
                    setBillings(updatedBillings);
                }

                setTotalCount(total);
                setPage(pageNum);
                setHasMore(updatedBillings.length < total);
            }
        } catch (err) {
            console.error('Error fetching billing data:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleFilterChange = (updates) => {
        // Update local state for responsiveness
        if ('dateFrom' in updates) setFilterDateFrom(updates.dateFrom);
        if ('dateTo' in updates) setFilterDateTo(updates.dateTo);
        if ('tracking' in updates) setFilterTracking(updates.tracking);
        if ('recipient' in updates) setFilterRecipient(updates.recipient);
        if ('createdBy' in updates) setFilterUser(updates.createdBy);
        if ('status' in updates) setFilterStatus(updates.status);
    };

    const loadMore = () => {
        fetchBillings(page + 1, false);
    };

    return (
        <div className="billing-container">
            <div className="billing-header">
                <button className="back-btn" onClick={onBack}>←</button>
                <h2>💵 Billing &amp; Shipment History</h2>
                {companySlug ? (
                    <span style={{ marginLeft: '1rem', fontSize: '0.8rem', background: '#bee3f8', color: '#2b6cb0', padding: '4px 10px', borderRadius: '20px', fontWeight: 700 }}>
                        🏢 {companySlug.toUpperCase()}
                    </span>
                ) : isSuperUser && (
                    <span style={{ marginLeft: '1rem', fontSize: '0.8rem', background: '#c6f6d5', color: '#276749', padding: '4px 10px', borderRadius: '20px', fontWeight: 700 }}>
                        👑 Viewing Global Records
                    </span>
                )}
            </div>

            {/* Filters Section */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap', padding: '1rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #edf2f7' }}>
                <input
                    type="date"
                    value={filterDateFrom}
                    onChange={e => handleFilterChange({ dateFrom: e.target.value })}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem' }}
                />
                <input
                    type="date"
                    value={filterDateTo}
                    onChange={e => handleFilterChange({ dateTo: e.target.value })}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem' }}
                />
                <input
                    placeholder="🔍 Tracking #"
                    value={filterTracking}
                    onChange={e => handleFilterChange({ tracking: e.target.value })}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', width: '130px' }}
                />
                <input
                    placeholder="👤 Recipient"
                    value={filterRecipient}
                    onChange={e => handleFilterChange({ recipient: e.target.value })}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', width: '130px' }}
                />
                <input
                    placeholder="📝 User / Created By"
                    value={filterUser}
                    onChange={e => handleFilterChange({ createdBy: e.target.value })}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', width: '130px' }}
                />
                <select
                    value={filterStatus}
                    onChange={e => handleFilterChange({ status: e.target.value })}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem' }}>
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="voided">Voided</option>
                </select>
                <button
                    onClick={() => {
                        fetchBillings(1, true, { dateFrom: filterDateFrom, dateTo: filterDateTo, tracking: filterTracking, recipient: filterRecipient, createdBy: filterUser, status: filterStatus });
                    }}
                    style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', background: '#3182ce', color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' }}
                >
                    Apply Filters
                </button>
                <button
                    onClick={() => {
                        setFilterDateFrom(''); setFilterDateTo(''); setFilterTracking('');
                        setFilterRecipient(''); setFilterUser(''); setFilterStatus('');
                        fetchBillings(1, true, { dateFrom: '', dateTo: '', tracking: '', recipient: '', createdBy: '', status: '' });
                    }}
                    style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', background: '#e2e8f0', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                >
                    Clear
                </button>
                <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '0.85rem', color: '#718096' }}>
                    Showing {billings.length} of {totalCount}
                </div>
            </div>

            {loading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading records...</p>
                </div>
            ) : billings.length === 0 ? (
                <div className="empty-state">
                    <p>No shipment records found.</p>
                </div>
            ) : (
                <div className="billing-content">
                    <table className="billing-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Tracking #</th>
                                <th>Service</th>
                                <th>Origin</th>
                                <th>Destination</th>
                                <th>Charges</th>
                                <th>Type</th>
                                <th>User</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billings.map((record) => (
                                <tr key={record._id || record.trackingNumber}
                                    style={{ opacity: record.status === 'voided' ? 0.55 : 1 }}>
                                    <td>{record.shipmentDate ? new Date(record.shipmentDate).toLocaleDateString() : 'N/A'}</td>
                                    <td className="tracking-cell">{record.trackingNumber}</td>
                                    <td>{record.service}</td>
                                    <td>{record.origin?.city}, {record.origin?.country}</td>
                                    <td>{record.destination?.city}, {record.destination?.country}</td>
                                    <td className="charges-cell">
                                        {record.currency} {parseFloat(record.totalCharges || 0).toFixed(2)}
                                    </td>
                                    <td>
                                        <span className={`badge ${record.isDDP ? 'badge-ddp' : 'badge-standard'}`}>
                                            {record.isDDP ? 'DDP' : 'Standard'}
                                        </span>
                                    </td>
                                    <td style={{ fontSize: '0.8rem', color: '#718096' }}>{record.user || record.createdBy}</td>
                                    <td>
                                        {record.status === 'voided'
                                            ? <span style={{ background: '#fed7d7', color: '#c53030', padding: '2px 8px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 700 }}>Voided</span>
                                            : <span style={{ background: '#c6f6d5', color: '#276749', padding: '2px 8px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 700 }}>Active</span>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {hasMore && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '1rem' }}>
                            <button
                                onClick={loadMore}
                                disabled={loadingMore}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    background: loadingMore ? '#cbd5e0' : '#3182ce',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                                    opacity: loadingMore ? 0.6 : 1,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {loadingMore ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Billing;
